import { useState, useEffect } from "react";
import { Modal, Button, Alert } from "antd";
import { IoShareOutline, IoPhonePortraitOutline } from "react-icons/io5";
import { AiOutlinePlus } from "react-icons/ai";
import { BsArrowDown } from "react-icons/bs";
import FirebaseService from "services/FirebaseService"; // Using your original working service
import { useMyContext } from "Context/MyContextProvider";

const PushNotificationButton = () => {
    const { api, authToken, UserData } = useMyContext();
    const [isRegistering, setIsRegistering] = useState(false);
    const [isRegistered, setIsRegistered] = useState(false);
    const [error, setError] = useState(null);
    const [deviceOS, setDeviceOS] = useState("");
    const [browser, setBrowser] = useState("");
    const [isStandalone, setIsStandalone] = useState(false);
    const [isSupported, setIsSupported] = useState(true);
    const [foregroundListener, setForegroundListener] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(() => {
        const lastClosed = localStorage.getItem("notificationModalLastClosed");
        if (!lastClosed) return true;

        const lastClosedTimestamp = parseInt(lastClosed);
        if (isNaN(lastClosedTimestamp)) return true;

        // Check if we have passed midnight of the day the modal was last closed
        const lastClosedDate = new Date(lastClosedTimestamp);
        const nextMidnight = new Date(lastClosedDate);
        nextMidnight.setHours(24, 0, 0, 0); // Sets to 00:00:00 of the next calendar day

        return Date.now() >= nextMidnight.getTime();
    });
    const handleCloseModal = () => {
        setShowModal(false);
        localStorage.setItem(
            "notificationModalLastClosed",
            Date.now().toString()
        );
    };

    // Check if notifications are supported
    const checkNotificationSupport = () => {
        return (
            'Notification' in window &&
            'serviceWorker' in navigator
        );
    };

    useEffect(() => {
        const detectDevice = () => {
            const userAgent = navigator.userAgent;
            const isInStandaloneMode =
                window.matchMedia("(display-mode: standalone)").matches ||
                window.navigator.standalone ||
                document.referrer.includes("android-app://");
            setIsStandalone(isInStandaloneMode);

            // Detect OS
            let os = "unknown";
            if (/iPhone|iPad|iPod/.test(userAgent)) os = "iOS";
            else if (/Android/.test(userAgent)) os = "Android";
            else if (/Windows/.test(userAgent)) os = "Windows";
            else if (/Mac/.test(userAgent)) os = "MacOS";
            setDeviceOS(os);

            // Detect browser
            let browserName = "unknown";
            if (/CriOS/.test(userAgent)) browserName = "Chrome_iOS";
            else if (/FxiOS/.test(userAgent)) browserName = "Firefox_iOS";
            else if (/EdgiOS/.test(userAgent)) browserName = "Edge_iOS";
            else if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent))
                browserName = "Safari";
            else if (/Chrome/.test(userAgent)) browserName = "Chrome";
            else if (/Firefox/.test(userAgent)) browserName = "Firefox";

            setBrowser(browserName);
        };
        // alert('Device OS: ' + deviceOS + ', Browser: ' + browser);
        const checkSupport = () => {
            const supported = checkNotificationSupport();
            setIsSupported(supported);

            if (!supported) {
                console.warn('Notifications not supported on this device/browser');
                return false;
            }
            return true;
        };

        const checkPermission = async () => {
            detectDevice();

            if (!checkSupport()) {
                setLoading(false);
                return;
            }

            // Check current notification permission
            if (!('Notification' in window)) {
                setError("Notifications are not supported on this device/browser.");
                setIsSupported(false);
                return;
            }

            const permission = Notification.permission;

            if (permission === "granted") {
                setIsRegistered(true);
                setShowModal(false);

                // Setup foreground message listener
                if (!foregroundListener) {
                    try {
                        const unsubscribe = FirebaseService.setupForegroundMessageListener();
                        setForegroundListener(unsubscribe);
                    } catch (error) {
                        console.warn('Error setting up foreground listener:', error);
                    }
                }

                // Check if we already have a token
                const existingToken = localStorage.getItem("fcm_token");
                if (!existingToken && UserData?.id && authToken) {
                    // Try to get token for existing permission
                    try {
                        await FirebaseService.setupNotifications(UserData.id, authToken, api);
                    } catch (error) {
                        console.warn('Error setting up existing notifications:', error);
                    }
                }
            } else if (permission === "denied") {
                setError("Notifications are blocked. Please enable them in your browser settings.");
            }
            setLoading(false);
        };

        checkPermission();

        // Cleanup function
        return () => {
            if (foregroundListener && typeof foregroundListener === 'function') {
                foregroundListener();
            }
        };
    }, [api, authToken, UserData, foregroundListener]);

    const handleEnableNotifications = async () => {
        if (isRegistering || isRegistered) {
            setShowModal(false);
            return;
        }

        if (!isSupported) {
            setError("Notifications are not supported on this device/browser.");
            return;
        }

        setIsRegistering(true);
        setError(null);

        try {
            let token = null;

            // Use the original FirebaseService
            if (UserData?.id && authToken) {
                token = await FirebaseService.setupNotifications(UserData.id, authToken, api);
            } else {
                // If no user data, still try to setup basic notifications
                const hasPermission = await FirebaseService.requestNotificationPermission();
                if (hasPermission) {
                    token = await FirebaseService.getFCMToken();
                    if (token) {
                        localStorage.setItem('fcm_token', token);
                    }
                }
            }

            if (token) {
                // Setup foreground message listener
                if (!foregroundListener) {
                    const unsubscribe = FirebaseService.setupForegroundMessageListener();
                    setForegroundListener(unsubscribe);
                }

                setIsRegistered(true);
                setShowModal(false);
                console.log('Notifications enabled successfully');
            } else {
                throw new Error("Failed to setup notifications");
            }
        } catch (error) {
            const errorMessage = error.message || "Failed to enable notifications";
            setError(errorMessage);
            console.error("Error enabling notifications:", error);
        } finally {
            setIsRegistering(false);
        }
    };

    const renderInstructions = () => {
        if (deviceOS === "iOS") {
            const isThirdPartyBrowser = [
                "Chrome_iOS",
                "Firefox_iOS",
                "Edge_iOS",
            ].includes(browser);

            if (isThirdPartyBrowser) {
                return (
                    <div className="p-3 d-flex flex-column align-items-center">
                        <IoPhonePortraitOutline size={48} className="text-primary mb-2" />
                        <h6 className="fw-bold">Browser Not Supported</h6>
                        <p className="text-muted small">
                            {browser.split("_")[0]} on iOS doesn't support notifications.
                        </p>
                        <Alert
                            message="Due to Apple restrictions, only Safari supports push notifications on iPhone and iPad. 
        Please open this website in Safari and add it to your Home Screen to enable notifications."
                            type="warning"
                            showIcon
                            className="mt-3"
                        />
                        <div className="mt-2 text-muted small">
                            Why? Apple only allows web push notifications in Safari, not in Chrome, Firefox, or Edge on iOS.
                        </div>
                    </div>
                );
            }

            if (browser === "Safari" && !isStandalone) {
                return (
                    <div className="p-3 text-center">
                        <IoPhonePortraitOutline size={48} className="text-primary mb-3" />
                        <h6 className="fw-bold">Add to Home Screen</h6>
                        <p className="text-muted small">Follow these quick steps:</p>

                        <div className="d-flex flex-column align-items-center gap-3 mt-3">
                            <div className="p-3 border rounded w-100">
                                <IoShareOutline size={24} className="text-primary" />
                                <div className="mt-2">
                                    <strong>Step 1:</strong> Tap the Share icon
                                </div>
                            </div>

                            <BsArrowDown size={20} className="text-muted" />

                            <div className="p-3 border rounded w-100">
                                <AiOutlinePlus size={24} className="text-primary" />
                                <div className="mt-2">
                                    <strong>Step 2:</strong> Select "Add to Home Screen"
                                </div>
                            </div>

                            <BsArrowDown size={20} className="text-muted" />

                            <div className="p-3 border rounded w-100">
                                <IoPhonePortraitOutline size={24} className="text-primary" />
                                <div className="mt-2">
                                    <strong>Step 3:</strong> Open from Home Screen
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }

            if (isStandalone || browser === "Safari") {
                return (
                    <div className="p-3 text-center">
                        <IoPhonePortraitOutline size={48} className="text-primary mb-2" />
                        <h6 className="fw-bold">Enable Notifications</h6>
                        <p className="text-muted small">
                            Allow notifications to stay updated with latest events
                        </p>
                    </div>
                );
            }
        }

        return (
            <div className="p-2">
                {error ? (
                    <div className="text-danger">{error}</div>
                ) : (
                    <p>Click below to enable browser notifications</p>
                )}
            </div>
        );
    };

    const showEnableButton = () => {
        if (deviceOS === "iOS") {
            if (isStandalone) return true;
            if (browser === "Safari" && !isStandalone) return false;
            return false;
        }
        return true;
    };
    return (
        <>
            {!loading && showModal && (
                <Modal
                    title="Enable Notifications"
                    open={showModal}
                    onCancel={handleCloseModal}
                    footer={[
                        showEnableButton() && (
                            <Button
                                key="enable"
                                type="primary"
                                onClick={handleEnableNotifications}
                                disabled={isRegistering || isRegistered}
                            >
                                {isRegistering
                                    ? "Enabling..."
                                    : isRegistered
                                        ? "Notifications Enabled"
                                        : "Enable Notifications"}
                            </Button>
                        ),
                        <Button key="close" onClick={handleCloseModal}>
                            Close
                        </Button>,
                    ]}
                    centered
                    maskClosable={false}
                >
                    {renderInstructions()}
                </Modal>
            )}
        </>
    );
};

export default PushNotificationButton;